/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import type { BufferedChangeset } from "ember-changeset/types";
import saveSendMessage from "parog-web/components/chat/utils/save-send-message";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import type AnalyticsService from "parog-web/services/analytics";
import type SemeiaSessionService from "parog-web/services/session";
interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  oldBioExamPrescription: BioExamPrescribedJournalEvent | undefined;
}

/* eslint-disable parog-web/migrate-non-patch-forms */
export default class BioExamPrescriptionEditionFormComponent extends JournalEventBasedComponent<Args> {
  @service declare analytics: AnalyticsService;
  @service declare session: SemeiaSessionService;
  @service declare router: RouterService;

  static fullComponentId = "prescription/bio-exam-edition-form";

  static forcDisplayName = "Prescription d'examens biologiques";

  static journalEventClass = BioExamPrescribedJournalEvent;

  declare chatMessage: string;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.chatMessage =
      "Bonjour, \nJe viens de vous éditer une nouvelle ordonnance. Elle est jointe à ce message. \nBonne journée.";
  }

  // Always dirty because we always are in creation mode
  get isDirty() {
    return true;
  }

  async save(): Promise<void> {
    this.forceQuit = true;
    // The validation doesn't work if we don't launch it manually
    this.args.changeset.validate();

    if (this.args.changeset.get("isValid")) {
      await this.args.changeset.save();
      await this.draft.onDidSave();

      const savedModel = this.args.changeset
        .data as BioExamPrescribedJournalEvent;
      if (savedModel.patient.authorizesAppUsage) {
        await saveSendMessage(
          { willSendMessage: true, chatMessage: this.chatMessage },
          {
            store: this.store,
            patient: savedModel.patient,
            analytics: this.analytics
          }
        );
        await this.store
          .createRecord("patient-chat-message-sent-journal-event", {
            occurredAt: new Date(),
            triggeringJournalEvent: savedModel,
            patient: savedModel.patient,
            body: "Nouvelle ordonnance reçue"
          })
          .save();
      }

      const alertsTriggered = this.store.peekAll(
        "alert-triggered-journal-event"
      );
      const alert = alertsTriggered.find(
        alert =>
          alert.triggeringJournalEvent?.id ==
          this.args.oldBioExamPrescription?.id
      );
      if (alert) {
        alert.processAlert({ actionTaken: "bio-exam-prescription-renewed" });
      }

      this.router.transitionTo(
        "medics-web.protected.patients.patient.prescriptions.bio-exam-display",
        savedModel.patient.id,
        savedModel.id
      );
    }
  }

  async _openPrescriptionInNewTab(
    savedModel: BioExamPrescribedJournalEvent
  ): Promise<void> {
    // Open the PDF prescription in a new tab
    if (savedModel) {
      this.router.replaceWith(
        "medics-web.protected.patients.patient.feature.component",
        this.model,
        "prescription",
        "bio-exam-display"
      );
    } else {
      throw new Error(
        `Impossible to open the PDF prescription because there is no JournalEvent found corresponding to the type : bio-exam-prescribed-journal-event}`
      );
    }
  }

  @action
  onCancelPrescription() {
    this.router.transitionTo(
      "medics-web.protected.patients.patient.dashboard",
      (this.args.changeset.data as BioExamPrescribedJournalEvent).patient
    );
  }
}
